import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import Pagination from '../ui/Pagination';
import TotalPageNumber from '../ui/TotalPageNumber';
import { usePagination } from '../../hooks/usePagination';
import { 
  Truck, 
  User, 
  Loader2, 
  AlertCircle, 
  Plus, 
  Upload, 
  CheckCircle, 
  UserCheck,
  Search,
  X,
  MapPin,
  Clock,
  Star,
  Edit,
  Trash2,
  Power,
  UserPlus
} from 'lucide-react';
import { apiService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import DriverRegistrationModal from './DriverRegistrationModal';
import BulkDriverRegistrationModal from './BulkDriverRegistrationModal';
import AmbulanceRegistrationModal from './AmbulanceRegistrationModal';
import BulkAmbulanceRegistrationModal from './BulkAmbulanceRegistrationModal';
import DriverAmbulanceAssignmentModal from './DriverAmbulanceAssignmentModal';

const DriversAndVehiclesTab = () => {
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activeView, setActiveView] = useState('all'); // 'all', 'drivers', 'vehicles'
  const [searchQuery, setSearchQuery] = useState('');
  const {
    page: driversPage, setPage: setDriversPage,
    limit: driversLimit, handleLimitChange: handleDriversLimitChange,
    pagination: driversPagination, setPagination: setDriversPagination,
  } = usePagination();

  const {
    page: vehiclesPage, setPage: setVehiclesPage,
    limit: vehiclesLimit, handleLimitChange: handleVehiclesLimitChange,
    pagination: vehiclesPagination, setPagination: setVehiclesPagination,
  } = usePagination();
  const [driversMetaCounts, setDriversMetaCounts] = useState({
    total_drivers: 0,
    available_drivers: 0,
    assigned_drivers: 0,
    unassigned_drivers: 0
  });
  const [vehiclesMetaCounts, setVehiclesMetaCounts] = useState({
    total: 0,
    available_vehicles: 0
  });
  const { user } = useAuth();

  // Fetch drivers data
  const fetchDrivers = useCallback(async () => {
    try {
      const response = await apiService.getMyDrivers({ limit: driversLimit, page: driversPage });
      if (response.success && response.data) {
        const transformedDrivers = response.data.map(driver => {
          const assignedAmbulance = driver.ambulance || driver.Ambulances;
          const plate = assignedAmbulance?.plate_number;
          return {
            id: driver.driver_id,
            name: driver.name,
            phone: driver.phone,
            vehicle: plate ? `Assigned (${plate})` : 'Unassigned',
            plateNumber: plate || null,
            status: driver.availability_status?.toLowerCase() || 'offline',
            shift: 'Day',
            tripsToday: driver.trip_count || 0,
            rating: driver.rating || 0,
            license_number: driver.license_number,
            experience_level: driver.experience_level,
            email: driver.email
          };
        });
        setDrivers(transformedDrivers);

        // Pagination from API: { meta: { page, limit, total, totalPages } }
        if (response.meta && typeof response.meta === 'object') {
          setDriversPagination({
            page: response.meta.page || driversPage,
            limit: response.meta.limit || driversLimit,
            total: response.meta.total || 0,
            totalPages: response.meta.totalPages || 1
          });
          // Driver counts from API meta
          setDriversMetaCounts({
            total_drivers: response.meta.total_drivers ?? response.meta.total ?? 0,
            available_drivers: response.meta.available_drivers ?? 0,
            assigned_drivers: response.meta.assigned_drivers ?? 0,
            unassigned_drivers: response.meta.unassigned_drivers ?? 0
          });
        } else {
          // Fallback when meta missing
          const total = transformedDrivers.length;
          const totalPages = Math.max(1, Math.ceil(total / (driversLimit || 10)));
          setDriversPagination({
            page: driversPage,
            limit: driversLimit,
            total,
            totalPages
          });
          setDriversMetaCounts({
            total_drivers: total,
            available_drivers: transformedDrivers.filter(d => d.status === 'available').length,
            assigned_drivers: 0,
            unassigned_drivers: transformedDrivers.filter(d => d.vehicle === 'Unassigned').length
          });
        }
        return;
      }
    } catch (apiError) {
      // Silently handle error, will show empty state
    }
    setDrivers([]);
    setDriversPagination((prev) => ({ ...prev, total: 0, totalPages: 1 }));
    setDriversMetaCounts({ total_drivers: 0, available_drivers: 0, assigned_drivers: 0, unassigned_drivers: 0 });
  }, [driversLimit, driversPage]);

  // Fetch vehicles data
  const fetchVehicles = useCallback(async () => {
    try {
      const operatorId = user?.operator?.operator_id || 
                        user?.operator_id || 
                        user?.operator?.id ||
                        user?.fleet_operator_id ||
                        user?.fleet_operator?.operator_id;
      
      // Try getMyVehicles first (fleet-specific endpoint)
      let response;
      try {
        response = await apiService.getMyVehicles({ 
          limit: vehiclesLimit,
          page: vehiclesPage
        });
      } catch (myVehiclesError) {
        // Fallback to getAmbulances
        response = await apiService.getAmbulances({ 
          limit: vehiclesLimit,
          page: vehiclesPage,
          operator_id: operatorId
        });
      }

      // Vehicle counts from API meta (GET /v1/driver/my-vehicles)
      if (response?.meta && typeof response.meta === 'object') {
        setVehiclesMetaCounts({
          total: response.meta.total ?? 0,
          available_vehicles: response.meta.available_vehicles ?? 0
        });

        // Pagination for vehicles (API-driven)
        setVehiclesPagination({
          page: response.meta.page || vehiclesPage,
          limit: response.meta.limit || vehiclesLimit,
          total: response.meta.total || 0,
          totalPages: response.meta.totalPages || 1
        });
      }
      
      let ambulances = [];
      if (response.success && response.data) {
        if (response.data.ambulances) {
          ambulances = response.data.ambulances;
        } else if (response.data.vehicles) {
          ambulances = response.data.vehicles;
        } else if (Array.isArray(response.data)) {
          ambulances = response.data;
        }
      } else if (response.ambulances) {
        ambulances = response.ambulances;
      } else if (response.vehicles) {
        ambulances = response.vehicles;
      } else if (Array.isArray(response)) {
        ambulances = response;
      }
      
      if (ambulances.length > 0) {
        const transformedVehicles = ambulances.map(ambulance => ({
          id: ambulance.vehicle_id || ambulance.id || ambulance.plate_number,
          vehicleId: ambulance.vehicle_id || ambulance.id,
          driver: ambulance.assigned_driver?.name || 'Unassigned',
          driverId: ambulance.assigned_driver?.driver_id,
          status: ambulance.status?.toLowerCase() || 'offline',
          location: ambulance.current_latitude && ambulance.current_longitude 
            ? `${Number(ambulance.current_latitude).toFixed(4)}, ${Number(ambulance.current_longitude).toFixed(4)}`
            : 'Unknown',
          lastUpdate: ambulance.updated_at ? new Date(ambulance.updated_at).toLocaleTimeString() : 'Unknown',
          trips: ambulance.trip_count || 0,
          plate_number: ambulance.plate_number,
          type: ambulance.type?.name || ambulance.ambulance_type?.name || 'Unknown',
          typeId: ambulance.type_id || ambulance.ambulance_type_id,
          rawData: ambulance // Store raw data for updates
        }));
        setVehicles(transformedVehicles);

        // Fallback pagination when meta missing
        if (!response?.meta || typeof response.meta !== 'object') {
          const total = transformedVehicles.length;
          const totalPages = Math.max(1, Math.ceil(total / (vehiclesLimit || 10)));
          setVehiclesPagination({
            page: vehiclesPage,
            limit: vehiclesLimit,
            total,
            totalPages
          });
        }
        return;
      }
      setVehicles([]);
      setVehiclesPagination((prev) => ({ ...prev, total: 0, totalPages: 1 }));
    } catch (apiError) {
      setVehicles([]);
      setVehiclesPagination((prev) => ({ ...prev, total: 0, totalPages: 1 }));
    }
  }, [user, vehiclesLimit, vehiclesPage]);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      await Promise.all([fetchDrivers(), fetchVehicles()]);
    } catch (err) {
      setError('Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [fetchDrivers, fetchVehicles]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // When paging/limit changes, refetch list views
  useEffect(() => {
    fetchDrivers();
  }, [driversPage, driversLimit, fetchDrivers]);

  useEffect(() => {
    fetchVehicles();
  }, [vehiclesPage, vehiclesLimit, fetchVehicles]);

  // Success and error handlers
  const handleDriverRegistrationSuccess = useCallback((data) => {
    setSuccessMessage(`Successfully registered driver: ${data.name || 'New driver'}`);
    setErrorMessage('');
    fetchAllData();
    setTimeout(() => setSuccessMessage(''), 5000);
  }, [fetchAllData]);

  const handleDriverRegistrationError = useCallback((error) => {
    setErrorMessage(error);
    setSuccessMessage('');
    setTimeout(() => setErrorMessage(''), 5000);
  }, []);

  const handleVehicleRegistrationSuccess = useCallback((data) => {
    setSuccessMessage(`Successfully registered ambulance: ${data.plate_number || 'New ambulance'}`);
    setErrorMessage('');
    fetchAllData();
    setTimeout(() => setSuccessMessage(''), 5000);
  }, [fetchAllData]);

  const handleVehicleRegistrationError = useCallback((error) => {
    setErrorMessage(error);
    setSuccessMessage('');
    setTimeout(() => setErrorMessage(''), 5000);
  }, []);

  const handleAssignmentSuccess = useCallback((data) => {
    const successfulCount = data.successful?.length || 0;
    const failedCount = data.failed?.length || 0;
    let message = `Successfully assigned ${successfulCount} drivers to ambulances.`;
    if (failedCount > 0) {
      message += ` ${failedCount} assignments failed.`;
    }
    setSuccessMessage(message);
    setErrorMessage('');
    fetchAllData();
    setTimeout(() => setSuccessMessage(''), 5000);
  }, [fetchAllData]);

  const handleAssignmentError = useCallback((error) => {
    setErrorMessage(error);
    setSuccessMessage('');
    setTimeout(() => setErrorMessage(''), 5000);
  }, []);

  // Vehicle management handlers
  const handleUpdateVehicleStatus = useCallback(async (vehicleId, newStatus) => {
    try {
      setIsLoading(true);
      await apiService.updateAmbulanceStatus(vehicleId, { status: newStatus });
      setSuccessMessage(`Vehicle status updated to ${newStatus}`);
      setErrorMessage('');
      fetchAllData();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      setErrorMessage(`Failed to update vehicle status: ${error.message || 'Unknown error'}`);
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsLoading(false);
    }
  }, [fetchAllData]);

  const handleDeleteVehicle = useCallback(async (vehicleId, plateNumber) => {
    if (!window.confirm(`Are you sure you want to delete vehicle ${plateNumber || vehicleId}? This action cannot be undone.`)) {
      return;
    }
    try {
      setIsLoading(true);
      await apiService.deleteAmbulance(vehicleId);
      setSuccessMessage(`Vehicle ${plateNumber || vehicleId} deleted successfully`);
      setErrorMessage('');
      fetchAllData();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      setErrorMessage(`Failed to delete vehicle: ${error.message || 'Unknown error'}`);
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsLoading(false);
    }
  }, [fetchAllData]);

  const handleAssignDriverToVehicle = useCallback(async (vehicleId, driverId) => {
    try {
      setIsLoading(true);
      await apiService.assignDriverToAmbulance(vehicleId, { driver_id: driverId });
      setSuccessMessage('Driver assigned to vehicle successfully');
      setErrorMessage('');
      fetchAllData();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      setErrorMessage(`Failed to assign driver: ${error.message || 'Unknown error'}`);
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsLoading(false);
    }
  }, [fetchAllData]);

  const handleRemoveDriverFromVehicle = useCallback(async (vehicleId) => {
    try {
      setIsLoading(true);
      await apiService.removeDriverFromAmbulance(vehicleId);
      setSuccessMessage('Driver removed from vehicle successfully');
      setErrorMessage('');
      fetchAllData();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      setErrorMessage(`Failed to remove driver: ${error.message || 'Unknown error'}`);
      setSuccessMessage('');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsLoading(false);
    }
  }, [fetchAllData]);

  // Filter data based on search
  const filteredDrivers = drivers.filter((driver) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (driver.name || '').toLowerCase().includes(query) ||
      (driver.phone || '').toLowerCase().includes(query) ||
      (driver.license_number || '').toLowerCase().includes(query) ||
      (driver.vehicle || '').toLowerCase().includes(query) ||
      (driver.email || '').toLowerCase().includes(query)
    );
  });

  const filteredVehicles = vehicles.filter((vehicle) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (vehicle.plate_number || '').toLowerCase().includes(query) ||
      (vehicle.driver || '').toLowerCase().includes(query) ||
      (vehicle.type || '').toLowerCase().includes(query) ||
      (vehicle.location || '').toLowerCase().includes(query)
    );
  });

  // Statistics
  const stats = {
    // Drivers from /v1/driver/my-drivers meta
    totalDrivers: driversMetaCounts.total_drivers || drivers.length,
    availableDrivers: driversMetaCounts.available_drivers || drivers.filter(d => d.status === 'available').length,
    assignedDrivers: driversMetaCounts.assigned_drivers || 0,
    unassignedDrivers: driversMetaCounts.unassigned_drivers || drivers.filter(d => d.vehicle === 'Unassigned').length,
    // Vehicles from /v1/driver/my-vehicles meta
    totalVehicles: vehiclesMetaCounts.total || vehicles.length,
    availableVehicles: vehiclesMetaCounts.available_vehicles || vehicles.filter(v => v.status === 'available').length
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin mr-2" />
        <span className="text-gray-600">Loading drivers and vehicles...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-800">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Total Drivers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalDrivers}</p>
              </div>
              <User className="w-8 h-8 text-blue-600 bg-blue-100 rounded-lg p-1.5" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Available</p>
                <p className="text-2xl font-bold text-green-600">{stats.availableDrivers}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600 bg-green-100 rounded-lg p-1.5" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Total Vehicles</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalVehicles}</p>
              </div>
              <Truck className="w-8 h-8 text-purple-600 bg-purple-100 rounded-lg p-1.5" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Available</p>
                <p className="text-2xl font-bold text-green-600">{stats.availableVehicles}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600 bg-green-100 rounded-lg p-1.5" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Assigned</p>
                <p className="text-2xl font-bold text-blue-600">{stats.assignedDrivers}</p>
              </div>
              <UserCheck className="w-8 h-8 text-blue-600 bg-blue-100 rounded-lg p-1.5" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Unassigned</p>
                <p className="text-2xl font-bold text-orange-600">{stats.unassignedDrivers}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-orange-600 bg-orange-100 rounded-lg p-1.5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Management Card */}
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-6 pt-4 sm:pt-6">
          <div>
            <CardTitle className="font-serif text-base sm:text-lg">Drivers & Vehicles Management</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Manage your drivers and ambulance fleet in one place.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <DriverRegistrationModal
              onSuccess={handleDriverRegistrationSuccess}
              onError={handleDriverRegistrationError}
            >
              <Button className="text-xs sm:text-sm flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Add Driver</span>
              </Button>
            </DriverRegistrationModal>
            <AmbulanceRegistrationModal
              onSuccess={handleVehicleRegistrationSuccess}
              onError={handleVehicleRegistrationError}
            >
              <Button className="text-xs sm:text-sm flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Add Vehicle</span>
              </Button>
            </AmbulanceRegistrationModal>
            <DriverAmbulanceAssignmentModal
              onSuccess={handleAssignmentSuccess}
              onError={handleAssignmentError}
            >
              <Button 
                variant="outline"
                className="text-xs sm:text-sm flex items-center space-x-2 border-green-600 text-green-600 hover:bg-green-50"
              >
                <UserCheck className="w-4 h-4" />
                <span>Assign</span>
              </Button>
            </DriverAmbulanceAssignmentModal>
          </div>
        </CardHeader>

        <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
          {/* View Toggle */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              variant={activeView === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveView('all')}
              className="text-xs sm:text-sm"
            >
              All ({filteredDrivers.length + filteredVehicles.length})
            </Button>
            <Button
              variant={activeView === 'drivers' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveView('drivers')}
              className="text-xs sm:text-sm"
            >
              Drivers ({filteredDrivers.length})
            </Button>
            <Button
              variant={activeView === 'vehicles' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveView('vehicles')}
              className="text-xs sm:text-sm"
            >
              Vehicles ({filteredVehicles.length})
            </Button>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Search drivers or vehicles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="space-y-6">
            {/* Drivers Section */}
            {(activeView === 'all' || activeView === 'drivers') && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Drivers ({filteredDrivers.length})
                  </h3>
                  <div className="flex gap-2">
                    <BulkDriverRegistrationModal
                      onSuccess={handleDriverRegistrationSuccess}
                      onError={handleDriverRegistrationError}
                    >
                      <Button variant="outline" size="sm" className="text-xs">
                        <Upload className="w-3 h-3 mr-1" />
                        Bulk
                      </Button>
                    </BulkDriverRegistrationModal>
                  </div>
                </div>

                {/* Drivers pagination (API-driven) */}
                <div className="mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Pagination
                        currentPage={driversPage}
                        totalPages={driversPagination.totalPages || 1}
                        onPageChange={setDriversPage}
                      />
                      <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                        Page {driversPage} of {driversPagination.totalPages || 1}
                      </span>
                    </div>
                    <div className="w-full sm:w-auto">
                      <TotalPageNumber
                        totalPages={driversPagination.totalPages || 1}
                        itemsPerPage={driversLimit}
                        onItemsPerPageChange={handleDriversLimitChange}
                      />
                    </div>
                  </div>
                </div>

                {filteredDrivers.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No drivers found.</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {filteredDrivers.map((driver) => (
                      <Card key={driver.id} className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-blue-600 font-semibold text-lg">
                                {driver.name?.charAt(0) || 'D'}
                              </span>
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{driver.name}</h4>
                              <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {driver.phone}
                                </span>
                                <span className={`flex items-center gap-1 ${driver.vehicle === 'Unassigned' ? 'text-gray-500' : 'text-emerald-700 font-medium'}`}>
                                  <Truck className="w-4 h-4" />
                                  {driver.vehicle}
                                </span>
                                {driver.rating > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Star className="w-4 h-4 text-yellow-500" />
                                    {driver.rating}/5
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                License: {driver.license_number} • {driver.tripsToday} trips today
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge 
                              variant={driver.status === 'available' ? 'default' : 'secondary'}
                              className={driver.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                            >
                              {driver.status}
                            </Badge>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Vehicles Section */}
            {(activeView === 'all' || activeView === 'vehicles') && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Truck className="w-5 h-5" />
                    Vehicles ({filteredVehicles.length})
                  </h3>
                  <div className="flex gap-2">
                    <BulkAmbulanceRegistrationModal
                      onSuccess={handleVehicleRegistrationSuccess}
                      onError={handleVehicleRegistrationError}
                    >
                      <Button variant="outline" size="sm" className="text-xs">
                        <Upload className="w-3 h-3 mr-1" />
                        Bulk
                      </Button>
                    </BulkAmbulanceRegistrationModal>
                  </div>
                </div>

                {/* Vehicles pagination (API-driven) */}
                <div className="mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Pagination
                        currentPage={vehiclesPage}
                        totalPages={vehiclesPagination.totalPages || 1}
                        onPageChange={setVehiclesPage}
                      />
                      <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                        Page {vehiclesPage} of {vehiclesPagination.totalPages || 1}
                      </span>
                    </div>
                    <div className="w-full sm:w-auto">
                      <TotalPageNumber
                        totalPages={vehiclesPagination.totalPages || 1}
                        itemsPerPage={vehiclesLimit}
                        onItemsPerPageChange={handleVehiclesLimitChange}
                      />
                    </div>
                  </div>
                </div>

                {filteredVehicles.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Truck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No vehicles found.</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {filteredVehicles.map((vehicle) => (
                      <Card key={vehicle.id} className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                              <Truck className="w-6 h-6 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-mono font-semibold text-gray-900">{vehicle.plate_number || vehicle.id}</h4>
                                <Badge variant="outline" className="text-xs">{vehicle.type}</Badge>
                              </div>
                              <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  {vehicle.driver}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {vehicle.location}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {vehicle.trips} trips
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Last update: {vehicle.lastUpdate}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge 
                              variant={vehicle.status === 'available' ? 'default' : 'secondary'}
                              className={vehicle.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                            >
                              {vehicle.status}
                            </Badge>
                            <div className="flex items-center gap-1">
                              {/* Sign off toggle commented out
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const newStatus = vehicle.status === 'available' ? 'offline' : 'available';
                                  handleUpdateVehicleStatus(vehicle.vehicleId || vehicle.id, newStatus.toUpperCase());
                                }}
                                className="h-8 w-8 p-0"
                                title={`Toggle ${vehicle.status === 'available' ? 'offline' : 'available'} status`}
                              >
                                <Power className="w-4 h-4" />
                              </Button>
                              */}
                              {vehicle.driver !== 'Unassigned' && vehicle.driverId && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRemoveDriverFromVehicle(vehicle.vehicleId || vehicle.id)}
                                  className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700"
                                  title="Remove driver"
                                >
                                  <User className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteVehicle(vehicle.vehicleId || vehicle.id, vehicle.plate_number)}
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                title="Delete vehicle"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DriversAndVehiclesTab;



